import React, { useEffect, useMemo, useState } from "react";
import { useGetOwnerDetail } from "../../hooks/ownerMangementApi/ownerDetails";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/content_wrapper/header";
import { EyeSvg } from "../../utils/svgComponent";
import CustomLaoder from "../../components/customLoader";

const KEY_STATUS_MAP: Record<number, { label: string; cls: string }> = {
  0: { label: "Waiting Dropoff", cls: "badge bg-warning text-dark" },
  1: { label: "In Keybox",       cls: "badge bg-success" },
  2: { label: "Not In Keybox",   cls: "badge bg-secondary" },
};

type SortField = "name" | "status" | "subscription" | "host" | null;
type SortDir   = "asc" | "desc";

const OwnersDetailScreen: React.FC = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const data       = location.state;

  useEffect(() => {
    if (!data?.id) navigate("/ownerlisting", { replace: true });
  }, [data, navigate]);

  const { data: GetDetail, isLoading, isError } = useGetOwnerDetail({ ownerId: data?.id });
  const OwnerData = GetDetail?.data?.ownerDetails;
  const allKeys: any[] = useMemo(() => GetDetail?.data?.keyList ?? [], [GetDetail]);

  /* ── Filters ── */
  const [search,      setSearch]      = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");   // "all" | "0" | "1" | "2"
  const [subFilter,   setSubFilter]   = useState<string>("all");     // "all" | "active" | "inactive"
  const [sortField,   setSortField]   = useState<SortField>(null);
  const [sortDir,     setSortDir]     = useState<SortDir>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <i className="fa-solid fa-sort ms-1 text-muted" style={{ fontSize: "0.7rem", opacity: 0.4 }} />;
    return sortDir === "asc"
      ? <i className="fa-solid fa-sort-up ms-1"   style={{ fontSize: "0.7rem", color: "#72bcb8" }} />
      : <i className="fa-solid fa-sort-down ms-1" style={{ fontSize: "0.7rem", color: "#72bcb8" }} />;
  };

  const filteredKeys = useMemo(() => {
    let keys = [...allKeys];

    if (search.trim()) {
      const q = search.toLowerCase();
      keys = keys.filter(k => (k.name || "").toLowerCase().includes(q));
    }
    if (statusFilter !== "all") {
      keys = keys.filter(k => String(k.status) === statusFilter);
    }
    if (subFilter !== "all") {
      keys = keys.filter(k =>
        subFilter === "active" ? k.isSubscriptionActive : !k.isSubscriptionActive
      );
    }

    if (sortField) {
      keys.sort((a, b) => {
        let av: any, bv: any;
        if (sortField === "name")         { av = (a.name || "").toLowerCase();  bv = (b.name || "").toLowerCase(); }
        if (sortField === "status")       { av = a.status ?? 99;                bv = b.status ?? 99; }
        if (sortField === "subscription") { av = a.isSubscriptionActive ? 0 : 1; bv = b.isSubscriptionActive ? 0 : 1; }
        if (sortField === "host")         { av = (a.keyHostName || "").toLowerCase(); bv = (b.keyHostName || "").toLowerCase(); }
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ?  1 : -1;
        return 0;
      });
    }

    return keys;
  }, [allKeys, search, statusFilter, subFilter, sortField, sortDir]);

  /* ── Summary stats (computed from all keys, not filtered) ── */
  const stats = useMemo(() => ({
    total:       allKeys.length,
    inKeybox:    allKeys.filter(k => k.status === 1).length,
    activeSubs:  allKeys.filter(k => k.isSubscriptionActive).length,
    expiredSubs: allKeys.filter(k => !k.isSubscriptionActive).length,
  }), [allKeys]);

  const Stat = ({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) => (
    <div style={{
      flex: "1 1 120px",
      background: "#fff",
      border: "1px solid #e9ecef",
      borderRadius: 10,
      padding: "14px 18px",
      display: "flex",
      alignItems: "center",
      gap: 12,
    }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={icon} style={{ color, fontSize: "1rem" }} />
      </div>
      <div>
        <div style={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.1, color: "#212529" }}>{value}</div>
        <div style={{ fontSize: "0.72rem", color: "#6c757d", marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );

  const FilterPill = ({
    label, active, color, onClick,
  }: { label: string; active: boolean; color: string; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: "999px",
        padding: "3px 12px",
        fontSize: "0.78rem",
        fontWeight: active ? 600 : 400,
        cursor: "pointer",
        border: `1px solid ${color}`,
        background: active ? color : "transparent",
        color: active ? "#fff" : color,
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="main-wrapper">
      <Header title="Owner Details" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section profile-section">
            <div className="container">
              <button
                className="btn primary-btn me-0 view-history-btn my-3"
                onClick={() => navigate(-1)}>
                <i className="fa-solid fa-chevron-left" />{" "}
                <span className="m-lg-1">Back</span>
              </button>

              {isError && (
                <div className="alert alert-danger" role="alert">
                  Owner not found or could not be loaded.
                </div>
              )}

              <div className="wrapper-card">
                <div className="row gy-3">

                  {/* ── Owner profile card ── */}
                  <div className="col-xl-12">
                    <div className="info-card">
                      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">

                        {/* Left: identity */}
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h3 className="mb-0" style={{ fontWeight: 700 }}>{OwnerData?.name || "—"}</h3>
                            <span className={`c-pill ${OwnerData?.userStatus === 1 ? "c-pill--success" : "c-pill--danger"}`}>
                              {OwnerData?.userStatus === 1 ? "Active" : "Suspended"}
                            </span>
                          </div>

                          <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                            {OwnerData?.email && (
                              <span>
                                <i className="fa-regular fa-envelope me-1" />
                                {OwnerData.email}
                              </span>
                            )}
                            {(OwnerData?.countryCode || OwnerData?.phoneNumber) && (
                              <span>
                                <i className="fa-solid fa-phone me-1" />
                                {OwnerData.countryCode ? `+${OwnerData.countryCode} ` : ""}{OwnerData.phoneNumber || ""}
                              </span>
                            )}
                            {OwnerData?.address && (
                              <span>
                                <i className="fa-solid fa-location-dot me-1" />
                                {OwnerData.address}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Right: quick stats strip */}
                        <div className="d-flex flex-wrap gap-2" style={{ minWidth: 0 }}>
                          <Stat icon="fa-solid fa-key"              label="Total Keys"   value={stats.total}       color="#72bcb8" />
                          <Stat icon="fa-solid fa-box"              label="In Keybox"    value={stats.inKeybox}    color="#198754" />
                          <Stat icon="fa-solid fa-circle-check"     label="Active Subs"  value={stats.activeSubs}  color="#0d6efd" />
                          <Stat icon="fa-solid fa-circle-xmark"     label="Expired Subs" value={stats.expiredSubs} color="#dc3545" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Keys table ── */}
                  <div className="col-xl-12">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <div className="sub-heading mb-0">
                        <h2 className="mb-0">Keys</h2>
                      </div>

                      {/* Search */}
                      <div style={{ position: "relative", minWidth: 220 }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search key name…"
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          style={{ paddingRight: search ? 28 : undefined }}
                        />
                        {search && (
                          <button
                            type="button"
                            onClick={() => setSearch("")}
                            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "#aaa", fontSize: 13, lineHeight: 1 }}
                          >
                            <i className="fa-solid fa-circle-xmark" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Filter pills row */}
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3" style={{ fontSize: "0.8rem" }}>
                      <span className="text-muted">Status:</span>
                      {[
                        { label: "All",              value: "all", color: "#6c757d" },
                        { label: "In Keybox",        value: "1",   color: "#198754" },
                        { label: "Not In Keybox",    value: "2",   color: "#6c757d" },
                        { label: "Waiting Dropoff",  value: "0",   color: "#fd7e14" },
                      ].map(f => (
                        <FilterPill key={f.value} label={f.label} active={statusFilter === f.value} color={f.color} onClick={() => setStatusFilter(f.value)} />
                      ))}

                      <span className="text-muted ms-3">Subscription:</span>
                      {[
                        { label: "All",     value: "all",      color: "#6c757d" },
                        { label: "Active",  value: "active",   color: "#0d6efd" },
                        { label: "Expired", value: "inactive", color: "#dc3545" },
                      ].map(f => (
                        <FilterPill key={f.value} label={f.label} active={subFilter === f.value} color={f.color} onClick={() => setSubFilter(f.value)} />
                      ))}

                      {(search || statusFilter !== "all" || subFilter !== "all") && (
                        <button
                          type="button"
                          onClick={() => { setSearch(""); setStatusFilter("all"); setSubFilter("all"); }}
                          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#dc3545", fontSize: "0.78rem", marginLeft: 4 }}
                        >
                          <i className="fa-solid fa-xmark me-1" />Clear filters
                        </button>
                      )}

                      <span className="text-muted ms-auto" style={{ fontSize: "0.75rem" }}>
                        {filteredKeys.length} of {allKeys.length} key{allKeys.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div className="table-blk">
                      <div className="table-responsive">
                        <table className="display border-0 w-100">
                          <thead>
                            <tr>
                              <th style={{ cursor: "pointer" }} onClick={() => handleSort("name")}>
                                Key Name <SortIcon field="name" />
                              </th>
                              <th>Key Ring</th>
                              <th style={{ cursor: "pointer" }} onClick={() => handleSort("status")}>
                                Status <SortIcon field="status" />
                              </th>
                              <th style={{ cursor: "pointer" }} onClick={() => handleSort("host")}>
                                Host <SortIcon field="host" />
                              </th>
                              <th>Box ID</th>
                              <th>Slot</th>
                              <th style={{ cursor: "pointer" }} onClick={() => handleSort("subscription")}>
                                Subscription <SortIcon field="subscription" />
                              </th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredKeys.map((item: any, index: number) => {
                              const statusBadge = KEY_STATUS_MAP[item.status] ?? { label: "Unknown", cls: "badge bg-light text-dark" };
                              return (
                                <tr key={index}>
                                  <td>{item.name || <span className="text-muted">—</span>}</td>

                                  <td>
                                    {item.keyringCode ? (
                                      <span
                                        className="font-monospace"
                                        title={item.keyringCode}
                                        style={{ display: "inline-block", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>
                                        {item.keyringCode}
                                      </span>
                                    ) : <span className="text-muted">—</span>}
                                  </td>

                                  <td>
                                    <span className={statusBadge.cls}>{statusBadge.label}</span>
                                  </td>

                                  <td>
                                    {item.keyHostName
                                      ? <span title={item.keyHostAddress || item.keyHostName} style={{ cursor: "default" }}>{item.keyHostName}</span>
                                      : <span className="text-muted">—</span>}
                                  </td>

                                  <td>{item.boxId || <span className="text-muted">—</span>}</td>
                                  <td>{item.boxLocation ?? <span className="text-muted">—</span>}</td>

                                  <td>
                                    <span className={`c-pill ${item.isSubscriptionActive ? "c-pill--success" : "c-pill--danger"}`}>
                                      {item.isSubscriptionActive ? "Active" : "Expired"}
                                    </span>
                                  </td>

                                  <td>
                                    <span className="table-action" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                      <a
                                        href="#"
                                        className="table-eye"
                                        title="View key details"
                                        style={{ cursor: "pointer" }}
                                        onClick={(e) => { e.preventDefault(); navigate("/keymanagementdetail", { state: { id: item._id } }); }}>
                                        <EyeSvg />
                                      </a>
                                      <a
                                        href="#"
                                        title="Investigate key"
                                        style={{ cursor: "pointer", color: "#72bcb8", textDecoration: "none" }}
                                        onClick={(e) => { e.preventDefault(); navigate("/key-investigation", { state: { id: item._id } }); }}>
                                        <i className="fa-solid fa-microscope" style={{ fontSize: "1rem" }} />
                                      </a>
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {filteredKeys.length === 0 && (
                        <div className="d-flex justify-content-center align-items-center my-4 sub-heading">
                          <h2 className="m-0">
                            {allKeys.length === 0 ? "No keys found" : "No keys match the current filters"}
                          </h2>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </section>
        </div>
      </CustomLaoder>
    </div>
  );
};

export default OwnersDetailScreen;
